import React, { useContext } from 'react';
import strings from '../../../strings/strings.json';
import Table, { TableRow } from 'components/table/Table';
import { EmptyTableContent } from 'components/table/EmptyTableContent';
import { breakpoints, sizes } from 'theme';
import { IconSize } from 'assets/icons/icon-sizes';
import { BodyRegular, FlexAlignCenter, FlexCol, FlexEnd, FlexMd, Headline3 } from 'components/Elements';
import MainSection from 'components/sections/MainSection';
import styled from 'styled-components';
import PlusIcon from 'assets/icons/controls/PlusIcon';
import Modal from 'components/modals/Modal';
import useDeleteRequest from 'hooks/useDeleteRequest';
import Alert from 'components/alert/Alert';
import { logError } from 'lib/debug-helpers';
import { PercentageProgressBar } from 'components/progressBar/ProgressBar';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import BuildIcon from 'assets/icons/BuildIcon';
import { useNavigate } from 'react-router-dom';
import { OADashboardContext } from 'contextProviders/OADashboardContext';
import BuildCourseSwitch from 'components/modals/createCourseModalComponents/BuildCourseSwitch';
import NextIcon from 'assets/icons/controls/NextIcon';
import Button from 'components/buttons/Button';

type oaCoursesTableProps = {
    activeRowCourseId?: string;
};

export default function OACoursesTable({ activeRowCourseId }: oaCoursesTableProps): JSX.Element {
    const courses = useContext(OADashboardContext).courses!;
    const { refetchCourses } = useContext(OADashboardContext);
    const navigate = useNavigate();

    const createCourseButtonRef = React.useRef<HTMLButtonElement>(null);
    const [showCreateCourseModal, setShowCreateCourseModal] = React.useState<boolean>(false);
    const [idOfCourseToDelete, setIdOfCourseToDelete] = React.useState<string | null>(null);
    const [nameOfCourseToDelete, setNameOfCourseToDelete] = React.useState<string>('');
    const [areYouSureOpen, setAreYouSureOpen] = React.useState<boolean>(false);
    const [deleteCourseError, setDeleteCourseError] = React.useState<boolean>(false);
    const [activeRowId, setActiveRowId] = React.useState<string | undefined>(activeRowCourseId);

    const { deleteData: deleteCourse, deleteInProgress } = useDeleteRequest(`courses/${idOfCourseToDelete}`);

    const scrollRef = React.createRef<any>();
    React.useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    });

    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        const someCoursesAreGenerating = courses.some((course) => {
            const courseIsGenerating =
                !!course.dateGenerateStart && !course.dateGenerateEnd && !course.dateGenerateError;
            return courseIsGenerating;
        });

        if (someCoursesAreGenerating && !intervalRef.current) {
            // Start polling
            intervalRef.current = setInterval(() => {
                refetchCourses();
            }, 4000);
        }
        if (!someCoursesAreGenerating && intervalRef.current) {
            // Stop polling
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [courses]); // eslint-disable-line

    const courseRows: TableRow[] = React.useMemo(() => {
        return courses
            .sort((a, b) => {
                // Sort by published status first (published courses at the top)
                if (a.published === b.published) {
                    // If both have the same published status, sort by dateCreated (newest first)
                    return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
                }
                return a.published ? -1 : 1;
            })
            .flatMap((course) => {
                const shortDate = new Date(course.dateCreated).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                });

                const viewPublishedCourseLink = `/published-course/${course.id}`;
                const viewPublishedCourseAria = `${strings.OACoursesTable.viewCourseDetailsButton.aria} ${course.title}`;
                const buildCourseLink = `/build-course/${course.id}`;
                const buildCourseAria = `${strings.OACoursesTable.buildCourseButton.aria}: ${course.title}`;
                const deleteCourseAria = `${strings.OACoursesTable.deleteCourseButton.aria}: ${course.title}`;

                let buttonSectionContent = <></>;

                if (!!course.dateGenerateError) {
                    buttonSectionContent = (
                        <>
                            <ErrorNotifierSlim description={strings.OACoursesTable.generateCourseError} />
                            <Button
                                title={strings.OACoursesTable.deleteCourseButton.title}
                                aria={deleteCourseAria}
                                colour={'BLACK'}
                                onClick={() => {
                                    setIdOfCourseToDelete(course.id);
                                    setNameOfCourseToDelete(course.title);
                                    setAreYouSureOpen(true);
                                }}
                            />
                        </>
                    );
                } else if (!!course.dateGenerateStart && !course.dateGenerateEnd) {
                    //course is generating
                    buttonSectionContent = (
                        <ProgressBarContainer>
                            <PercentageProgressBar
                                label={strings.OACoursesTable.generateProgressBarLabel}
                                expectedMinsToComplete={1}
                                dateStart={course.dateGenerateStart!}
                            />
                        </ProgressBarContainer>
                    );
                } else
                    buttonSectionContent = (
                        <>
                            {!course.published ? (
                                <DeleteAndBuildButtons>
                                    <Button
                                        title={strings.OACoursesTable.deleteCourseButton.title}
                                        aria={deleteCourseAria}
                                        colour={'BLACK'}
                                        onClick={() => {
                                            setIdOfCourseToDelete(course.id);
                                            setNameOfCourseToDelete(course.title);
                                            setAreYouSureOpen(true);
                                        }}
                                    />
                                    <Button
                                        title={strings.OACoursesTable.buildCourseButton.title}
                                        aria={buildCourseAria}
                                        icon={<BuildIcon />}
                                        onClick={() => navigate(buildCourseLink)}
                                    />
                                </DeleteAndBuildButtons>
                            ) : (
                                <Button
                                    title={strings.OACoursesTable.viewCourseDetailsButton.title}
                                    aria={viewPublishedCourseAria}
                                    icon={<NextIcon />}
                                    onClick={() => {
                                        navigate(viewPublishedCourseLink);
                                    }}
                                    iconOnRight
                                />
                            )}
                        </>
                    );

                return {
                    id: course.id,
                    cells: [
                        {
                            name: strings.OACoursesTable.tableHeaders.courseTitle,
                            value: course.title,
                        },
                        {
                            name: strings.OACoursesTable.tableHeaders.createdBy,
                            value: (
                                <FlexCol ref={activeRowCourseId === course.id ? scrollRef : null}>
                                    <BodyRegular
                                        $bold
                                    >{`${course.user?.firstName} ${course.user?.lastName}`}</BodyRegular>
                                    <BodyRegular>{shortDate}</BodyRegular>
                                </FlexCol>
                            ),
                        },
                        {
                            name: '',
                            value: <ButtonsSection>{buttonSectionContent}</ButtonsSection>,
                        },
                    ],
                };
            });
    }, [courses]); //eslint-disable-line

    async function onDelete() {
        const response = await deleteCourse();

        if (response.errors) {
            logError(response.errors);
            setDeleteCourseError(true);
            return;
        }

        setIdOfCourseToDelete(null);
        setAreYouSureOpen(false);
        setDeleteCourseError(false);
        await refetchCourses();
    }

    function closeModal() {
        setShowCreateCourseModal(false);
        createCourseButtonRef.current?.focus();
    }

    async function closeModalOnCreateCourse(courseId: string) {
        await refetchCourses();
        setActiveRowId(courseId);
        setShowCreateCourseModal(false);
    }

    return (
        <CourseTableContentSection>
            <TitleAndButtonContainer>
                <Headline3>{strings.OACoursesTable.coursesHeading}</Headline3>
                {courses.length > 0 && (
                    <Button
                        title={strings.OACoursesTable.createCourseButton.title}
                        aria={strings.OACoursesTable.createCourseButton.aria}
                        onClick={() => setShowCreateCourseModal(true)}
                        icon={<PlusIcon size={IconSize.LARGE} />}
                        colour="PRIMARY"
                    />
                )}
            </TitleAndButtonContainer>
            <div>
                <Table
                    headers={[
                        {
                            name: strings.OACoursesTable.tableHeaders.courseTitle,
                            rowPercentage: 35,
                        },
                        {
                            name: strings.OACoursesTable.tableHeaders.createdBy,
                            rowPercentage: 25,
                        },
                        {
                            name: '',
                            rowPercentage: 40,
                            mobileRightColumn: true,
                        },
                    ]}
                    rows={courseRows}
                    emptyTableContent={
                        <EmptyTableContent
                            title={strings.OACoursesTable.emptyTableContent.title}
                            buttonTitle={strings.OACoursesTable.createCourseButton.title}
                            buttonAria={strings.OACoursesTable.createCourseButton.aria}
                            buttonOnClick={() => setShowCreateCourseModal(true)}
                            buttonIcon={<PlusIcon size={IconSize.LARGE} />}
                        />
                    }
                    mobileViewBelow={breakpoints.md}
                    activeRow={activeRowId}
                />
            </div>

            {showCreateCourseModal && (
                <Modal
                    onClickCloseButton={closeModal}
                    headlineText={strings.createCourseModal.createCourseHeadline}
                    modalBody={<BuildCourseSwitch closeModal={closeModalOnCreateCourse} />}
                />
            )}

            {areYouSureOpen && (
                <Alert
                    alertText={`${strings.OACoursesTable.deleteCourseAlert.alertText} ${nameOfCourseToDelete}?`}
                    buttonText={strings.OACoursesTable.deleteCourseAlert.deleteButtonTitle}
                    ariaButtonText={strings.OACoursesTable.deleteCourseAlert.deleteButtonAria}
                    onSubmit={onDelete}
                    submitting={deleteInProgress}
                    closeAlert={() => {
                        setIdOfCourseToDelete(null);
                        setNameOfCourseToDelete('');
                        setAreYouSureOpen(false);
                    }}
                    error={
                        deleteCourseError
                            ? {
                                  title: strings.OACoursesTable.deleteCourseError.title,
                                  description: strings.OACoursesTable.deleteCourseError.description,
                              }
                            : undefined
                    }
                />
            )}
        </CourseTableContentSection>
    );
}

const DeleteAndBuildButtons = styled(FlexMd)`
    width: 100%;
    justify-content: end;
    @media (max-width: ${breakpoints.md}) {
        flex-direction: column-reverse;
    }
`;

const ProgressBarContainer = styled.div`
    width: 300px;
    @media (max-width: ${breakpoints.sm}) {
        width: 100%;
    }
`;

const ButtonsSection = styled(FlexEnd)`
    width: 100%;
    gap: ${sizes.spacingRg};
`;

const CourseTableContentSection = styled(MainSection)`
    gap: ${sizes.spacingRg};
    padding: ${sizes.spacingSm} 0;
    min-height: 120px;

    @media (max-width: ${breakpoints.sm}) {
        padding: unset;
    }
`;

const TitleAndButtonContainer = styled(FlexAlignCenter)`
    justify-content: space-between;
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingMd};
        align-items: flex-start;
    }
`;
